import { Link, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import {
  LayoutDashboard, FileText, ShoppingCart, Users, Truck, Package,
  BookOpen, ScrollText, Wallet, UserSquare2, BarChart3, Building2,
  LogOut, ChevronDown, Calculator, Settings2, LibraryBig
} from "lucide-react";
import { useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";

const navGroups = [
  {
    label: "Principal",
    items: [{ to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" }],
  },
  {
    label: "Comercial",
    items: [
      { to: "/ventas", icon: FileText, label: "Ventas", testid: "nav-ventas" },
      { to: "/compras", icon: ShoppingCart, label: "Compras", testid: "nav-compras" },
      { to: "/clientes", icon: Users, label: "Clientes", testid: "nav-clientes" },
      { to: "/proveedores", icon: Truck, label: "Proveedores", testid: "nav-proveedores" },
      { to: "/productos", icon: Package, label: "Productos / Almacén", testid: "nav-productos" },
    ],
  },
  {
    label: "Contabilidad",
    items: [
      { to: "/plan-contable", icon: BookOpen, label: "Plan Contable (PCGE)", testid: "nav-plan-contable" },
      { to: "/asientos", icon: ScrollText, label: "Asientos Contables", testid: "nav-asientos" },
      { to: "/libros-contables", icon: LibraryBig, label: "Informes Contables", testid: "nav-libros" },
      { to: "/caja-bancos", icon: Wallet, label: "Caja y Bancos", testid: "nav-caja" },
    ],
  },
  {
    label: "Recursos Humanos",
    items: [
      { to: "/empleados", icon: UserSquare2, label: "Empleados / Planillas", testid: "nav-empleados" },
    ],
  },
  {
    label: "Análisis",
    items: [
      { to: "/reportes", icon: BarChart3, label: "Reportes", testid: "nav-reportes" },
    ],
  },
  {
    label: "Configuración",
    items: [
      { to: "/tablas", icon: Settings2, label: "Tablas maestras", testid: "nav-tablas" },
      { to: "/empresas", icon: Building2, label: "Empresas (Multi-RUC)", testid: "nav-empresas" },
    ],
  },
];

export default function Layout({ children }) {
  const { user, empresa, empresas, switchEmpresa, logout } = useApp();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`${collapsed ? "w-16" : "w-64"} shrink-0 bg-slate-900 text-slate-200 flex flex-col h-screen sticky top-0 transition-all`}
      >
        <div className="h-14 flex items-center gap-2 px-4 border-b border-slate-800">
          <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center">
            <Calculator className="w-4 h-4 text-white" />
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="text-[13px] font-bold text-white tracking-tight">CONTAPE</span>
              <span className="text-[10px] text-slate-400">Sistema Contable Perú</span>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {navGroups.map((g) => (
            <div key={g.label} className="mb-3">
              {!collapsed && (
                <div className="px-4 mb-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  {g.label}
                </div>
              )}
              <ul className="space-y-0.5 px-2">
                {g.items.map((it) => (
                  <li key={it.to}>
                    <NavLink
                      to={it.to}
                      end={it.to === "/"}
                      data-testid={it.testid}
                      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                    >
                      <it.icon className="w-4 h-4 shrink-0" />
                      {!collapsed && <span className="truncate">{it.label}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800 text-[10px] text-slate-500">
          {!collapsed ? `v1.0 • ${new Date().getFullYear()}` : "v1"}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header data-testid="topbar" className="h-14 bg-white border-b border-slate-200 px-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              data-testid="toggle-sidebar"
              onClick={() => setCollapsed((c) => !c)}
              className="text-slate-600 hover:text-slate-900 text-sm px-2 py-1 rounded hover:bg-slate-100"
            >
              ☰
            </button>
            <div className="hidden md:flex items-center gap-2 text-[11px] text-slate-500 tabular">
              <span>Periodo:</span>
              <span className="font-bold text-slate-800">{new Date().toLocaleString("es-PE", { month: "long", year: "numeric" })}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {empresas.length > 0 && (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                <Select
                  value={empresa?.id || ""}
                  onValueChange={(val) => {
                    const e = empresas.find((x) => x.id === val);
                    if (e) switchEmpresa(e);
                  }}
                >
                  <SelectTrigger data-testid="company-select" className="h-8 w-[260px] text-[12px]">
                    <SelectValue placeholder="Selecciona empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {empresas.map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`company-opt-${e.ruc}`}>
                        <span className="font-mono text-[11px] mr-2">{e.ruc}</span>{e.razon_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-100">
                  <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-[11px] flex items-center justify-center font-bold">
                    {(user?.name || "U").charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden md:flex flex-col items-start leading-tight">
                    <span className="text-[12px] font-semibold text-slate-800">{user?.name}</span>
                    <span className="text-[10px] text-slate-500">{user?.role}</span>
                  </div>
                  <ChevronDown className="w-3 h-3 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/empresas")} data-testid="menu-empresas">
                  <Building2 className="w-3.5 h-3.5 mr-2" /> Mis empresas
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-rose-600">
                  <LogOut className="w-3.5 h-3.5 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-5 overflow-x-auto">
          {!empresa && empresas.length === 0 ? (
            <div className="max-w-xl mx-auto mt-12 bg-white border border-slate-200 rounded-md p-8 text-center">
              <Building2 className="w-10 h-10 mx-auto text-slate-400 mb-3" />
              <h2 className="text-xl font-bold text-slate-800 mb-2">Configura tu primera empresa</h2>
              <p className="text-sm text-slate-600 mb-4">Antes de continuar, crea una empresa con tu RUC. Luego podrás gestionar ventas, compras y contabilidad.</p>
              <Link to="/empresas" data-testid="cta-crear-empresa" className="inline-flex items-center bg-slate-900 text-white text-sm px-4 py-2 rounded-md hover:bg-slate-800">
                Crear empresa
              </Link>
            </div>
          ) : (
            children
          )}
        </main>
      </div>
    </div>
  );
}
